import React, { useMemo, useRef, useState, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import * as consts from 'config/consts';
import { formatNumber, formatDate } from 'utils/formatter';
import { IncomeGainItemProps } from 'pages/DividendDeposit';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import {
  marker,
  LineChartLegend,
  CircleChartLegend,
} from 'components/features/utils/chartUtils';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import {
  incomeGainTransitionDataProps,
  incomeGainTransitionItemProps,
  SelectedChartProps,
  selectedChartTypes,
} from './DividendTransition';
import { dataToSeries } from '../dividendPrediction/DividendChart';

type chartDataProps = {
  type: string | number;
  items: {
    x: number | undefined;
    y: number | null;
  }[];
};

// カラムチャート
export const DividendColumnChart = ({
  data,
  selectedChart,
}: {
  data: incomeGainTransitionDataProps[];
  selectedChart: SelectedChartProps;
}) => {
  const chartData: chartDataProps[] = useMemo(() => {
    // total
    const totalDataList = data.map(data_ => {
      return {
        x: yyyymmddToDate(data_.date)?.getTime(),
        y: data_.total,
      };
    });
    const sumTotalData = totalDataList.reduce((acc, current) => {
      const amount =
        acc[acc.length - 1] === undefined ? 0 : acc[acc.length - 1].y;

      const item = {
        x: current.x,
        y: amount + current.y,
      };
      return [...acc, item];
    }, [] as { x: number | undefined; y: number }[]);

    // memo: デザイン調整のため、受取合計はy: 0のデータを最初に差込む
    const initialTotalData = [{ x: sumTotalData[0].x, y: 0 }] as {
      x: number | undefined;
      y: number;
    }[];
    const totalData = initialTotalData.concat(sumTotalData);

    const sumAmount = (data: incomeGainTransitionItemProps[]) => {
      return data
        .map(item => (item.amount === null ? 0 : item.amount))
        .reduce((acc, current) => acc + current, 0);
    };

    if (selectedChart === selectedChartTypes.口座) {
      // 口座
      // SBI
      const sbiData = data.map(data_ => {
        const list = data_.items.filter(item => item.brokerID === 'SBI');
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // RAKUTEN
      const rakutenData = data.map(data_ => {
        const list = data_.items.filter(item => item.brokerID === 'RAKUTEN');
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      return [
        {
          type: 'sbi',
          items: sbiData,
        },
        {
          type: 'rakuten',
          items: rakutenData,
        },
        {
          type: 'total',
          items: totalData,
        },
      ];
    } else {
      // 商品種別
      // 国内株式
      const stockData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.国内株式
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 投資信託
      const investmentTrustData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.投資信託
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 外国株式
      const foreignStockData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.外国株式
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 未上場株式
      const unlistedData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.未上場株式
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 国内ETF・ETN
      const eftEtnData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes['国内ETF・ETN']
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 外国ETF・ETN
      const foreignetfEtnData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes['外国ETF・ETN']
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 国内REIT
      const reitData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.国内REIT
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 外国REIT
      const foreignReitData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.外国REIT
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 国内債券
      const bondData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.国内債券
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 外国債券
      const foreignBondData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.外国債券
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 国内EB
      const ebData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.国内EB
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      // 外国EB
      const foreigEbData = data.map(data_ => {
        const list = data_.items.filter(
          item => item.productType === consts.productTypes.外国EB
        );
        return {
          x: yyyymmddToDate(data_.date)?.getTime(),
          y: sumAmount(list),
        };
      });

      return [
        {
          type: consts.productTypes.国内株式,
          items: stockData,
        },
        {
          type: consts.productTypes.投資信託,
          items: investmentTrustData,
        },
        {
          type: consts.productTypes.外国株式,
          items: foreignStockData,
        },
        {
          type: consts.productTypes.未上場株式,
          items: unlistedData,
        },
        {
          type: consts.productTypes['国内ETF・ETN'],
          items: eftEtnData,
        },
        {
          type: consts.productTypes['外国ETF・ETN'],
          items: foreignetfEtnData,
        },
        {
          type: consts.productTypes.国内REIT,
          items: reitData,
        },
        {
          type: consts.productTypes.外国REIT,
          items: foreignReitData,
        },
        {
          type: consts.productTypes.国内債券,
          items: bondData,
        },
        {
          type: consts.productTypes.外国債券,
          items: foreignBondData,
        },
        {
          type: consts.productTypes.国内EB,
          items: ebData,
        },
        {
          type: consts.productTypes.外国EB,
          items: foreigEbData,
        },
        {
          type: 'total',
          items: totalData,
        },
      ];
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChart, data]);

  const getSeries = () => {
    if (selectedChart === selectedChartTypes.口座) {
      const arr_ = [
        {
          name: '受取合計',
          data: chartData.find(data => data.type === 'total')?.items ?? [],
          color: '#1EF571',
          type: 'line',
          step: 'center',
          yAxis: 1,
          zIndex: 10,
        },
        {
          name: '楽天証券',
          data: chartData.find(data => data.type === 'rakuten')?.items ?? [],
          color: '#E94E4E',
          type: 'column',
        },
        {
          name: 'SBI証券',
          data: chartData.find(data => data.type === 'sbi')?.items ?? [],
          color: '#4953DF',
          type: 'column',
        },
      ];

      const arr = arr_.filter(
        item => !item.data.every(data => data.y === 0)
      ) as Highcharts.SeriesOptionsType[];

      return arr;
    } else {
      const arr_ = [
        {
          name: '受取合計',
          data: chartData.find(data => data.type === 'total')?.items ?? [],
          color: '#1EF571',
          type: 'line',
          step: 'center',
          yAxis: 1,
          zIndex: 10,
        },
        {
          name: '外国EB',
          data:
            chartData.find(data => data.type === consts.productTypes.外国EB)
              ?.items ?? [],
          color: '#91BEAB',
          type: 'column',
        },
        {
          name: '国内EB',
          data:
            chartData.find(data => data.type === consts.productTypes.国内EB)
              ?.items ?? [],
          color: '#4098B4',
          type: 'column',
        },
        {
          name: '外国債券',
          data:
            chartData.find(data => data.type === consts.productTypes.外国債券)
              ?.items ?? [],
          color: '#F0C22B',
          type: 'column',
        },
        {
          name: '国内債券',
          data:
            chartData.find(data => data.type === consts.productTypes.国内債券)
              ?.items ?? [],
          color: '#26E016',
          type: 'column',
        },
        {
          name: '外国REIT',
          data:
            chartData.find(data => data.type === consts.productTypes.外国REIT)
              ?.items ?? [],
          color: '#D09494',
          type: 'column',
        },
        {
          name: '国内REIT',
          data:
            chartData.find(data => data.type === consts.productTypes.国内REIT)
              ?.items ?? [],
          color: '#FE5F5F',
          type: 'column',
        },
        {
          name: '外国ETF・ETN',
          data:
            chartData.find(
              data => data.type === consts.productTypes['外国ETF・ETN']
            )?.items ?? [],
          color: '#17CECE',
          type: 'column',
        },
        {
          name: '国内ETF・ETN',
          data:
            chartData.find(
              data => data.type === consts.productTypes['国内ETF・ETN']
            )?.items ?? [],
          color: '#6D71DA',
          type: 'column',
        },
        {
          name: '未上場株式',
          data:
            chartData.find(data => data.type === consts.productTypes.未上場株式)
              ?.items ?? [],
          color: '#8C8C8C',
          type: 'column',
        },
        {
          name: '外国株式',
          data:
            chartData.find(data => data.type === consts.productTypes.外国株式)
              ?.items ?? [],
          color: '#3FA9F5',
          type: 'column',
        },
        {
          name: '投資信託',
          data:
            chartData.find(data => data.type === consts.productTypes.投資信託)
              ?.items ?? [],
          color: '#DD80D4',
          type: 'column',
        },
        {
          name: '国内株式',
          data:
            chartData.find(data => data.type === consts.productTypes.国内株式)
              ?.items ?? [],
          color: '#179A8D',
          type: 'column',
        },
      ];

      const arr = arr_.filter(
        item => !item.data.every(data => data.y === 0)
      ) as Highcharts.SeriesOptionsType[];

      return arr;
    }
  };

  const [options, setOptions] = useState<Highcharts.Options>({
    chart: {
      type: 'column',
      animation: {
        duration: 0,
      },
      height: 220,
      marginLeft: 40,
      backgroundColor: '#ffffff',
    },
    credits: {
      enabled: false,
    },
    exporting: { enabled: false },
    rangeSelector: {
      enabled: false,
    },
    xAxis: {
      ordinal: false,
      type: 'datetime',
      gridLineWidth: 0,
      dateTimeLabelFormats: {
        year: '%Y',
        month: '%Y/%m',
        day: '%m/%d',
        week: '%m/%d',
        hour: '%m/%d',
        minute: '%m/%d',
        second: '%m/%d',
        millisecond: '%m/%d',
      },
      labels: {
        y: 22,
        formatter: function(e): string {
          return typeof this.value === 'number'
            ? formatDate(new Date(this.value), 'yyyy年M月')
            : '';
        },
      },
    },
    yAxis: [
      {
        allowDecimals: true,
        labels: {
          align: 'left',
          x: -30,
          y: 4,
          useHTML: true,
          formatter: function(): string {
            return typeof this.value === 'number'
              ? commaFormat(this.value / 10000, { numberOfDecimals: 1 })
              : '';
          },
        },
        title: {
          text: '(万円)',
          align: 'high',
          offset: 0,
          rotation: 0,
          x: -5,
          y: 3,
        },
        tickAmount: 4,
        showLastLabel: false,
        opposite: false,
        alignTicks: false,
      },
      {
        allowDecimals: true,
        labels: {
          align: 'left',
          x: 3,
          y: 4,
          useHTML: true,
          formatter: function(): string {
            return typeof this.value === 'number'
              ? commaFormat(this.value / 10000, { numberOfDecimals: 1 })
              : '';
          },
          style: {
            color: '#1EF571',
          },
        },
        title: {
          text: '(万円)',
          align: 'high',
          offset: 0,
          rotation: 0,
          y: 3,
          x: 3,
          style: { color: '#1EF571' },
        },
        tickAmount: 4,
        showLastLabel: false,
        opposite: true,
        alignTicks: false,
      },
    ],
    plotOptions: {
      column: {
        stacking: 'normal',
        borderRadius: 2,
      },
      line: {
        marker: {
          enabled: false,
        },
      },
      series: {
        turboThreshold: 15000,
      },
    },
    navigator: {
      enabled: false,
      maskFill: 'rgba(203, 213, 225, 0.6)',
    },
    title: {
      text: '',
    },
    legend: {
      enabled: true,
      align: 'left',
      verticalAlign: 'top',
      useHTML: true,
      itemDistance: 0,
      y: -15,
      margin: 10,
      itemStyle: { fontSize: '10px', fontWeight: 'normal' },
      maxHeight: 54,
      reversed: selectedChart === selectedChartTypes.口座 ? true : false,
      className: 'override-highcharts-legend common-margin-top',
      navigation: {
        activeColor: '#fdba74',
        arrowSize: 12,
        style: { color: '#000000', x: 20 },
      },
      labelFormatter: function() {
        if (this instanceof Highcharts.Series) {
          const name = this.name;
          const color = (() => {
            const t = this as unknown;
            if (
              typeof t === 'object' &&
              t &&
              'color' in t &&
              typeof (t as { color: unknown }).color === 'string'
            ) {
              return (t as { color: string }).color;
            }
            return '';
          })();

          if (name === '受取合計') {
            return renderToStaticMarkup(
              <LineChartLegend color={color}>{name}</LineChartLegend>
            );
          } else {
            return renderToStaticMarkup(
              <CircleChartLegend color={color}>{name}</CircleChartLegend>
            );
          }
        }
        return '';
      },
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      outside: true,
      formatter: function(): string {
        const Tooltip = ({
          date,
          children,
        }: {
          date: Date;
          children: React.ReactNodeArray;
        }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gap: '2px',
                  gridAutoRows: 'min-content',
                  gridAutoFlow: 'row',
                }}>
                <div style={{ paddingBottom: '3px', fontSize: '9px' }}>
                  {date.getFullYear()}/{date.getMonth() + 1}
                </div>
                {children}
              </div>
            </>
          );
        };
        const TooltipItem = ({
          name,
          color,
          value,
        }: {
          name: string;
          color: string;
          value: number;
        }): React.ReactElement => {
          const num = Math.ceil(value / 100) * 100;
          const label = `${commaFormat(num / 10000, {
            numberOfDecimals: 2,
          })}万円`;
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'min-content min-content min-content',
                  placeItems: 'center start',
                  gap: '2px',
                }}>
                <div
                  style={{
                    borderRadius: '50%',
                    height: '5px',
                    width: '5px',
                    gridColumn: 1,
                    backgroundColor: color,
                  }}
                />
                <span style={{ fontSize: '10px', gridColumn: 2 }}>{name}:</span>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: '10px',
                    gridColumn: 3,
                  }}>
                  {label}
                </span>
              </div>
            </>
          );
        };
        return renderToStaticMarkup(
          <Tooltip date={new Date(this.x)}>
            {(this.points ? this.points.slice().reverse() : []).map(
              (point, key) => (
                <TooltipItem
                  name={point.series.name}
                  color={point.color.toString()}
                  value={point.y}
                  key={key}
                />
              )
            )}
          </Tooltip>
        );
      },
      shared: true,
    },
    series: getSeries(),
  });

  useEffect(() => {
    setOptions({
      ...options,
      legend: {
        ...options.legend,
        reversed: true,
      },
      series: getSeries(),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  const ref = useRef<HighchartsReact.RefObject>(null);

  return (
    <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
  );
};

// パイチャート
type PieChartDataProps = {
  key: string;
  ratio: number;
};

type IncomeGainItemChartProps = Exclude<IncomeGainItemProps, 'amount'> & {
  amount: number;
};

export const DividendPieChartNoData = () => {
  return (
    <div className="flex">
      <img
        src="/static/images/dividend/dividend_pie_chart_no_data.png"
        alt="パイチャートのイメージ"
        className="mx-auto"
        height="255"
        width="255"
      />
    </div>
  );
};

export const DividendPieChart = ({
  incomeGainData,
}: {
  incomeGainData: IncomeGainItemProps[];
}) => {
  const chartData = useMemo(() => {
    const displayDividendData = incomeGainData.filter(
      data => data.amount !== null
    ) as IncomeGainItemChartProps[];

    const totalDividend = displayDividendData.reduce(
      (acc, v) => acc + v.amount,
      0
    );
    const displayItemIndex = 19;
    if (displayDividendData.length < displayItemIndex) {
      const chartData = displayDividendData.map(item => {
        return { key: item.symbolName, ratio: item.amount / totalDividend };
      });

      return chartData;
    } else {
      const targetDividendData = displayDividendData.filter(
        (_, index) => index < displayItemIndex
      );
      const displayDividenChartData = targetDividendData.map(item => {
        return { key: item.symbolName, ratio: item.amount / totalDividend };
      });

      const otherDividendData = displayDividendData.filter(
        (_, index) => index >= displayItemIndex
      );
      const otherTotalDividend = otherDividendData.reduce(
        (acc, v) => acc + v.amount,
        0
      );
      const otherIndex = displayDividendData.length - displayItemIndex;
      const otherDividendChartData = {
        key: `その他（${formatNumber(otherIndex, {})}件）`,
        ratio: otherTotalDividend / totalDividend,
      };
      const chartData = displayDividenChartData.concat(
        otherDividendChartData
      ) as PieChartDataProps[];

      return chartData;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomeGainData]);

  const series = dataToSeries(chartData.filter(datum => datum.ratio >= 0));

  const options = {
    exporting: { enabled: false },
    credits: { enabled: false },
    title: {
      text: '',
    },
    subtitle: {
      text: '',
    },
    legend: {
      enabled: true,
      align: 'right',
      verticalAlign: 'middle',
      layout: 'vertical',
      itemMarginTop: 10,
      itemStyle: {
        fontWeight: 'normal',
        fontSize: '10px',
        color: '#ffffff',
      },
    },
    chart: {
      type: 'column',
      height: 255,
    },
    tooltip: {
      outside: true,
      formatter(): string {
        return `${this.point.name}: ${commaFormat(this.point.percentage ?? 0, {
          numberOfDecimals: 1,
        })}%`;
      },
    },
    plotOptions: {
      series: {
        ...marker,
        borderColor: '#ffffff',
        borderWidth: 2,
        turboThreshold: 15000,
      },
      pie: {
        innerSize: '50%',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>',
          style: {
            fontWeight: 'normal',
          },
        },
      },
    },
    series,
  } as Highcharts.Options;

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <>
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
    </>
  );
};
